import { request, requestBlob } from '../client';
import { endpoints } from '../endpoints';
import { asList } from './shape';

/** The filters the respondents list and CSV share: status, target group and the days people entered (`from` / `to`, YYYY-MM-DD, both inclusive, read in time zone `tz`). */
const filtersOf = ({ status, targetGroupId, from, to, tz } = {}) => ({
  status: status || undefined,
  targetGroupId: targetGroupId || undefined,
  from: from || undefined,
  to: to || undefined,
  tz: (from || to) && tz ? tz : undefined,
});

/**
 * One page of a campaign's respondents (GET /project/{id}/respondents), newest first as the server orders them.
 * `page` is 1-based. Resolves { items, total, page, size, totalPages }; 404 (empty body) -> an empty page.
 * @param {string} projectId
 * @param {{ status?: string, targetGroupId?: string, from?: string, to?: string, tz?: string, page?: number, size?: number }} [options]
 * @param {{ signal?: AbortSignal }} [opts]
 */
export const listRespondents = async (projectId, { status, targetGroupId, from, to, tz, page = 1, size = 50 } = {}, opts) => {
  const raw = await request(endpoints.projects.respondents(projectId), { ...opts, query: { ...filtersOf({ status, targetGroupId, from, to, tz }), page, size } });
  const items = (raw && raw.content) || [];
  return {
    items,
    total: raw && typeof raw.totalElements === 'number' ? raw.totalElements : items.length,
    page: raw && typeof raw.page === 'number' ? raw.page : page,
    size: raw && typeof raw.size === 'number' ? raw.size : size,
    totalPages: raw && typeof raw.totalPages === 'number' ? raw.totalPages : 1,
  };
};

/**
 * The respondents as a CSV file (same `status` / `targetGroupId` / `from` / `to` / `tz` filters as the list).
 * Fetched with the app's headers rather than linked to, so it keeps working when the server enforces tokens.
 * Resolves { blob, filename }: `filename` is the server's (Content-Disposition, now exposed through CORS) or null.
 * @param {string} projectId
 * @param {{ status?: string, targetGroupId?: string, from?: string, to?: string, tz?: string }} [filters]
 * @param {{ signal?: AbortSignal }} [opts]
 */
export const downloadRespondentsCsv = (projectId, filters, opts) =>
  requestBlob(endpoints.projects.respondentsCsv(projectId), { ...opts, query: filtersOf(filters) });

/**
 * Correct respondents' final statuses. It takes effect immediately on the server and every change is kept in the
 * respondent's `history` (source "reconciliation", with the old status). Invalid items do not stop the others.
 * Only final statuses can be set, not `started`. Resolves { updated, failed, results: [{ id, ok?, error? ... }] }.
 * @param {string} projectId
 * @param {{ id: string, status: string, reason?: string }[]} items
 */
export const reconcileRespondents = async (projectId, items) => {
  const raw = await request(endpoints.projects.reconcile(projectId), { method: 'POST', body: items });
  return {
    updated: raw && typeof raw.updated === 'number' ? raw.updated : 0,
    failed: raw && typeof raw.failed === 'number' ? raw.failed : 0,
    results: (raw && Array.isArray(raw.results)) ? raw.results : [],
  };
};

/** The status keys with their descriptions (GET /reference/response-statuses). */
export const listResponseStatuses = async (opts) => asList(await request(endpoints.reference.responseStatuses(), opts));
